import React, { Suspense } from "react";
import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { LazyLoadComponent } from "react-lazy-load-image-component";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useTheme } from "@emotion/react";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";

import PageNotFound from "./PageNotFound";
import RouteProtection from "./RouteProtection";
import ScrollToTop from "./ScrollToTop";
import SignupCompletionGuard from "./SignupCompletionGuard";
import useInjectSchema from "./useInjectSchema";
import NavBar from "../components/layout/navbar";
import Footer from "../components/layout/footer";
import useTimer from "../components/hooks/useTimer";
import { Loader } from "../components/common/loader";
import RoleProtectedRoute from "./RoleProtectedRoute";
import { setMetaDataForSEO } from "./setMetaDataForSEO";
import { loadCmsSeoCache } from "../components/helpers/cmsSeoService";
import MiniSidebar from "../components/userProfile/index";
import AlertDialog from "../components/common/AlertDialog";
import AdminSideBar from "../components/admin/AdminSideBar";
import { brokerAgentsAction } from "../redux/slices/brokers";
import LeadPage from "../components/lead-page";
import UserAdminNavBar from "../components/layout/userAdminNavbar";
import TryDomainNavBar from "../components/layout/TryDomainNavBar";
import InvestorPricing from "../components/pricing/InvestorPricing";
import ServicerSideBar from "../components/Service-Provider/sideBar";
import { userSigupAction } from "../redux/slices/user/userSignUpSlice";
import { IS_TAILWIND_COMPONENT } from "../components/helpers/constants";
import CookieConsentBanner from "../components/common/CookieConsentBanner";
import { propertiesAction } from "../redux/slices/properties/propertiesSlice";
import ProfessionalSideBar from "../components/professional/ProfessionalSideBar";
import { exportCSVDetialsAction } from "../redux/slices/export-csv/exportcsvSlice";
import { Header as TailwindHeader } from "../components/TailwindComponents/Header";
import { Footer as TailwindFooter } from "../components/TailwindComponents/Footer";
import ServiceProvidersAdminNavbar from "../components/layout/serviceProvidersAdminNavbar";
import { adminDashboardAction } from "../redux/slices/admin/admin-dashboard/adminDashboardSlice";
import { propertyRiskManagementAction } from "../redux/slices/admin/property-risk-management/propertyRiskManagementSlice";
import {
  ALLOWED_PREFIXES,
  DEFAULT_URL,
  ROLES,
  SERVER,
  SUBDOMAIN,
  isCarHost,
  isServicerLocalDevHost,
} from "../components/helpers/constants";
import {
  injectFAQSchema,
  injectLandingPageOrganizationSchema,
  injectLocalBusinessSchema,
  injectRatingSchema,
} from "../components/helpers/schema";
import {
  decryptPayload,
  encryptPayload,
  findCountyLocation,
  findCountyLocationData,
  getNativeAppSafePath,
  getObjLength,
  guardNativeLoginRoute,
  isIncompleteSignupSession,
  isNativeAppShell,
  isNativeBlockedMarketingPath,
} from "../components/helpers/utils";
import { clearComplimentaryMonitoringActivated } from "../components/helpers/affiliateSignup";
import { partnerActivateRedirectPath } from "../components/partner-activate/partnerActivateRedirect";
import {
  applyCallPilotVisibilityForPath,
  startCallPilotAdminHideObserver,
} from "../components/helpers/callPilotChatbot";
import {
  CAR_HEADER_PROPS,
  adminRoutes,
  blogRoutesV2,
  carHostRoutes,
  forServiceProvicerRoute,
  lannerPublicRoutes,
  lannerRestrictedRoutes,
  loggedInUserRoutes,
  professionalRoutes,
  publicRoutes,
  publicRoutesV2,
  serviceProviderLoginRoutes,
  serviceProviderPublicRoutes,
  serviceProviderRoutes,
  tryDomainRoutes,
  userAdminRoutes,
} from "./routerLists";
import {
  registerAppNavigate,
  unregisterAppNavigate,
} from "../navigation/appNavigation";

const RootRouter = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.down(992));

  //initializes the timer states for hook
  const { timer, startTimer, stopTimer, formatTimeForResendOTP } = useTimer(10);
  const navigate = useNavigate();

  useEffect(() => {
    registerAppNavigate(navigate);
    return () => unregisterAppNavigate();
  }, [navigate]);

  useEffect(() => {
    guardNativeLoginRoute();
  }, [location.pathname]);

  useEffect(() => {
    const partnerActivatePath = partnerActivateRedirectPath(
      location.pathname,
      location.search,
    );
    if (partnerActivatePath) {
      navigate(partnerActivatePath, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // states
  const [openAlert, setOpenAlert] = useState(true);
  const [flag, setFlag] = useState(true);
  const [siteURL, setSiteURL] = useState(window?.location?.href);
  const [schemaData, setSchemaData] = useState(null);
  const [inValidBroker, setInValidBroker] = useState(false);
  const [custom_prefix_nav_links, setCustomPrefixNavLinks] =
    useState(undefined);

  // store data
  const { countyData } = useSelector((state) => state?.propertyRiskManagement);
  const { profileLoader } = useSelector((state) => state?.userProfile);
  const { brokerAgentLoader, brokerDetails } = useSelector(
    (state) => state?.brokerAgents,
  );

  // constants
  const siteHost = window.location.host;
  const pathname = window.location.pathname?.toLocaleLowerCase();
  const isAdminRoute =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/admins") ||
    pathname.startsWith("/customer-service");
  const addUserPath = pathname?.split("/")?.[1];
  const countyCode = pathname?.split("/")?.[2];
  console.log(countyCode, "pathname===>");
  localStorage.setItem("path", encryptPayload(pathname));
  const searchParams = new URLSearchParams(location.search);
  const planRenewal = searchParams.get("plan_renewal");
  const fromParam = searchParams.get("from");
  const valuOf = searchParams.keys().next().value;
  const code = fromParam || searchParams.get("code") || "";

  if (planRenewal) {
    localStorage.setItem("plan_renewal", planRenewal);
  }

  if (fromParam === "ios" || fromParam === "android") {
    localStorage.setItem("isMobile", encryptPayload(fromParam));
  }

  const isBot = /bot|crawl|slurp|spider/i.test(navigator.userAgent);

  const user = localStorage.getItem("authorised");
  const userPreferences =
    localStorage.getItem("preference") === null ||
    localStorage.getItem("preference").length === 0;

  // Extract prefix and company from URL pathname
  const pathParts = pathname.split("/").filter(Boolean); // Remove empty strings
  let prefix = null;
  let company = null;

  // Check if URL matches pattern: /prefix/company/common_routes
  prefix = pathParts[0];
  if (pathParts?.length >= 2) {
    company = pathParts[1];
  }
  // If there's a prefix parameter, validate it using the new ALLOWED_PREFIXES structure
  const allowedPrefixObj = ALLOWED_PREFIXES?.find(
    (item) => item?.prefix === prefix?.toLocaleLowerCase(),
  );
  console.log(prefix, "title_prefix==>", allowedPrefixObj);

  // Eldorado route protection: only allow /county-recorder/eldorado
  useEffect(() => {
    const isEldoradoRoute = pathname.includes("/eldorado");
    const isAllowedEldoradoRoute = pathname === "/county-recorder/eldorado";

    if (isEldoradoRoute && !isAllowedEldoradoRoute) {
      // Redirect to homepage if route contains eldorado but is not the allowed route
      navigate("/", { replace: true });
    }
  }, [pathname, navigate]);

  useEffect(() => {
    // County landing pages only — skip on the servicer portal (paths like
    // /clients/create would otherwise be treated as a county code).
    if (isServicerLocalDevHost()) {
      return;
    }

    if (
      countyCode &&
      countyCode !== "create-account" &&
      countyCode !== "selling-refinancing" &&
      countyCode !== "property-risk-management" &&
      countyCode !== "property-risk-report" &&
      countyCode !== "properties" &&
      countyCode !== "pricing" &&
      addUserPath !== "add-user-details" &&
      !pathname.includes("admin") &&
      !pathname.includes("report-results-investor") &&
      !pathname.includes("investor") &&
      !pathname.includes("investor-login") &&
      !pathname.includes("investor-list") &&
      !pathname.includes("title") &&
      !pathname.includes("insurance") &&
      !pathname.includes("blog") &&
      !pathname.includes("broker") &&
      !getObjLength(allowedPrefixObj) &&
      !pathname?.includes("/attorney/") &&
      // CAR invitation pages (/car-invite/view, /decline, /checkout) are not counties.
      !pathname?.startsWith("/car-invite/")
    ) {
      dispatch(
        propertyRiskManagementAction.countyData({
          url: `county/details`,
          data: { county: (countyCode || "").toString().toLowerCase() },
        }),
      );
    }
    if (
      (!countyCode ||
        //countyCode === "pricing" ||
        pathname.includes("insurance") ||
        pathname.includes(allowedPrefixObj?.prefix)) &&
      countyCode === "pricing" &&
      allowedPrefixObj &&
      !(
        location.pathname.includes("prager") ||
        location.pathname.includes("gallagher") ||
        location.pathname.includes("gorka")
      )
    ) {
      dispatch(
        userSigupAction.getSubscriptionPlan({
          url: "get-plans",
          data: {},
          type: "post",
        }),
      );
    }
  }, [countyCode, dispatch]);

  useEffect(() => {
    if (getObjLength(countyData)) {
      if (countyData?.success) {
        dispatch(
          userSigupAction.getSubscriptionPlan({
            url: `county/${countyData?.data?.id}/get-plans`,
            data: {},
            type: "post",
          }),
        );
      }
    }
  }, [countyData, dispatch]);

  const userPreference = (access) => {
    localStorage.setItem("preference", access);
    setOpenAlert(false);
    document.getElementById("HeaderNavbar")?.classList.remove("top_40");
    document.getElementById("adminHeader")?.classList.remove("top_40");
    document.getElementById("risk_report_navbar")?.classList.remove("top_40");
  };
  console.log(valuOf, "--code-->", code);

  const manageMobileCookieBannerPosition = () => {
    if (
      openAlert &&
      (localStorage.getItem("preference") === null ||
        localStorage.getItem("preference").length === 0)
    ) {
      document.getElementById("HeaderNavbar")?.classList.add("top_40");
      document.getElementById("adminHeader")?.classList.add("top_40");
    } else {
      document.getElementById("HeaderNavbar")?.classList.remove("top_40");
      document.getElementById("adminHeader")?.classList.remove("top_40");
      document.getElementById("risk_report_navbar")?.classList.remove("top_40");
    }
  };

  function cookieConsent() {
    return (
      <CookieConsentBanner
        isVisible={openAlert}
        onAccept={() => userPreference(true)}
        onDecline={() => userPreference(false)}
        userPreferences={userPreferences}
        isTab={isTab}
        code={code}
      />
    );
  }

  function Mobile_CookieConsent() {
    return (
      <>
        {openAlert &&
          userPreferences &&
          isTab &&
          code !== "ios" &&
          code !== "android" && (
            <Box className="mobile_cookie_bar">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CookieOutlinedIcon fontSize="small" />
                <Typography variant="body2" className="cookie_texts">
                  Our website uses cookies
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={() => userPreference(true)}>
                  Accept
                </Button>
                <Button size="small" onClick={() => userPreference(false)}>
                  Reject
                </Button>
              </Box>
            </Box>
          )}
      </>
    );
  }

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     console.log('Cookie called');
  //     setFlag(true);
  //     // cookieConsent();
  //   }, 5000);

  //   // Cleanup the timer if the component unmounts before the timeout is finished
  //   return () => clearTimeout(timer);
  // }, []);

  //For Invalid Broker code
  const closeBrokersModal = () => {
    dispatch(brokerAgentsAction.fetchBrokerData({}));
    setInValidBroker(false);
    stopTimer();
    navigate(`/`);
  };

  useEffect(() => {
    //Organization Page Schema
    const existingLandingPageSchema = document.getElementById(
      "organization-schema",
    );
    if (!existingLandingPageSchema) {
      injectLandingPageOrganizationSchema();
    }

    //Rating Page Schema
    const existingRatingSchema = document.getElementById("rating-schema");
    if (!existingRatingSchema) {
      injectRatingSchema();
    }

    //Local Business Schema
    const existingLocalBusinessSchema = document.getElementById(
      "local-business-schema",
    );
    if (!existingLocalBusinessSchema) {
      injectLocalBusinessSchema();
    }

    //FAQ Schema
    const existingFAQSchema = document.getElementById("main-faq-schema");
    if (!existingFAQSchema) {
      injectFAQSchema();
    }
  }, []);

  useEffect(() => {
    startCallPilotAdminHideObserver();
  }, []);

  // Native app shell must never render the public marketing homepage.
  useEffect(() => {
    if (!isNativeAppShell() || !isNativeBlockedMarketingPath(pathname)) {
      return;
    }
    navigate(getNativeAppSafePath(), { replace: true });
  }, [navigate, pathname]);

  // Check change of pathname
  useEffect(() => {
    console.log("pathname==>", pathname);
    if (!pathname.includes("property-monitoring-activated")) {
      clearComplimentaryMonitoringActivated();
    }
    if (
      pathname === "/" ||
      pathname === "/equity-protect" ||
      getObjLength(allowedPrefixObj) ||
      countyCode === "eldorado"
    ) {
      const navbar = document.getElementById("HeaderNavbar");

      localStorage.removeItem("scrp_hp");
      localStorage.removeItem("county-code");
      //if (navbar && navbar?.classList?.contains("sticky")) {
      navbar?.classList?.remove("sticky");
      if (navbar?.classList?.contains("bg-dark")) {
        navbar.classList.remove("bg-dark");
        navbar.classList.add("bg-transparent");
      }
      dispatch(propertyRiskManagementAction.setNavbarColor(false));
      //  }
    }

    // for clear the proeprty risk form redux values
    if (!pathname.includes("property-risk")) {
      dispatch(
        propertyRiskManagementAction.clearPropertyRiskManagementData({}),
      );
    }

    if (!pathname.includes("create-account")) {
      localStorage.removeItem("plain_signup");
      localStorage.removeItem("survey_email");
      localStorage.removeItem("isResumeSignup");
      localStorage.removeItem("OwnerVerifiedScreen");
      if (!pathname.includes("property-monitoring-activated")) {
        const preserveSignupSession = isIncompleteSignupSession();
        if (!preserveSignupSession) {
          sessionStorage.removeItem("userSignupDetails");
          localStorage.removeItem("userSignupDetails");
          // Paid county-site tag (eldoradoSignup COUNTY_SITE_PAID_SIGNUP_STORAGE_KEY).
          localStorage.removeItem("county_site_paid_signup");
          localStorage.removeItem("currentStep");
          localStorage.removeItem("user_Id");
          localStorage.removeItem("errorCount");
          localStorage.removeItem("skip");
          localStorage.removeItem("completed");
          localStorage.removeItem("promoCode");
          dispatch(userSigupAction.clearData());
          dispatch(userSigupAction.clearResumeProcessData());
        }
        // dispatch(userSigInAction?.clearStoreData());
        // dispatch(
        //   propertyRiskManagementAction.resetPropertyRiskManagementState(),
        // );
      }
    }
    if (
      !pathname.includes("create-account") &&
      !pathname.includes("property-risk") &&
      !pathname.includes("property-risk-report") &&
      !pathname.includes("property-monitoring-activated") &&
      !pathname.includes("add-new-users")
    ) {
      if (!isIncompleteSignupSession()) {
        localStorage.removeItem("category");
        localStorage.removeItem("signup_product_tier");
        localStorage.removeItem("property_search_data");
        localStorage.removeItem("searched_address");
        localStorage.removeItem("investor_id");
        localStorage.removeItem("county_user_id");
        localStorage.removeItem("user_details");
        localStorage.removeItem("isRiskAssessment");
      }
    }
    if (
      !pathname.includes("professional-properties") &&
      !pathname.includes("servicer-properties") &&
      !pathname.includes("servicer-scans") &&
      !pathname.includes("servicer-subscriptions") &&
      !(
        pathname === "/properties" ||
        pathname.startsWith("/user-profile") ||
        pathname.startsWith("/subscriptions") ||
        pathname.startsWith("/scans") ||
        pathname.startsWith("/documents") ||
        pathname.startsWith("/order-sign") ||
        pathname.startsWith("/communication") ||
        pathname.startsWith("/sub-user") ||
        pathname.startsWith("/professionals") ||
        pathname.startsWith("/settings") ||
        pathname.startsWith("/requested-documents") ||
        pathname.startsWith("/logs")
      )
    ) {
      dispatch(propertiesAction.resetState());
    }

    if (
      code === "req_doc_email" ||
      [...searchParams.values()].includes("req_doc_email")
    ) {
      localStorage.setItem("code", "req_doc_email");
    }

    const keepsPropertyReportFilters =
      pathname.includes("admin-properties") ||
      pathname.startsWith("/admin/reports");
    if (!keepsPropertyReportFilters) {
      localStorage.removeItem("tempPropertyFilterData");
      dispatch(adminDashboardAction.getSubscriptionModuleFilterData({}));
    }
    const keepsUserReportFilters =
      pathname.includes("admin-users") || pathname.startsWith("/admin/users");
    if (!keepsUserReportFilters) {
      localStorage.removeItem("tempUsersFilterData");
      dispatch(adminDashboardAction.getUsersModuleFilterData({}));
    }
    const keepsMonitoringReportFilters =
      pathname.includes("admin-property-monitoring") ||
      pathname.startsWith("/admin/reports");
    if (!keepsMonitoringReportFilters) {
      localStorage.removeItem("tempMonitoringFilterData");
      dispatch(adminDashboardAction.getMonitoringPropertiesFilterData({}));
    }
    if (
      !pathname.includes("agent-create-account") ||
      !pathname.includes("attorney-create-account")
    ) {
      dispatch(brokerAgentsAction.fetchAgentCreateAccData({}));
      dispatch(brokerAgentsAction.fetchAgentProfileListData({}));
    }
    if (
      pathname?.includes("broker") ||
      pathname.includes("/title/") ||
      pathname?.includes("/attorney/") ||
      pathname?.includes("/insurance/") ||
      (getObjLength(allowedPrefixObj) && allowedPrefixObj?.url_prefix)
    ) {
      let source = pathname.split("/")?.[1];
      let brokerName = pathname.split("/")?.[2];
      if (
        getObjLength(allowedPrefixObj) &&
        !allowedPrefixObj?.dynamic_companyName
      ) {
        source = allowedPrefixObj?.source;
        brokerName = allowedPrefixObj?.prefix;
      }

      const URL = `get-broker-details/${brokerName}?source=${source}`;

      dispatch(
        brokerAgentsAction.getBrokerDetails({
          url: URL,
          data: { source },
        }),
      );
    } else {
      localStorage.removeItem("agentauthToken");
      localStorage.removeItem("agent_role");
      localStorage.removeItem("agent_id");
    }
    if (!pathname.includes("investor")) {
      localStorage.removeItem("inv_token");
      localStorage.removeItem("is_from_investorList");
    }

    // Hide consumer chat widgets on admin and partner routes
    const apex_wrapper = document.getElementById(
      "apexchat_prechat_invitation_wrapper",
    );
    const hideChatWidgets = applyCallPilotVisibilityForPath(pathname);
    if (hideChatWidgets) {
      if (apex_wrapper) {
        apex_wrapper.style.display = "none";
      }
    } else if (apex_wrapper) {
      apex_wrapper.style.display = "block";
    }
    //For Mobile cookie banner
    manageMobileCookieBannerPosition();

    dispatch(exportCSVDetialsAction.getExportCSVFile({})); // cleared the common export csv redux values
  }, [pathname]);

  // to track the valid Broker on Broker's route
  useEffect(() => {
    if (
      (pathname?.includes("title") ||
        pathname?.includes("broker") ||
        pathname?.includes("/attorney/") ||
        pathname?.includes("insurance")) &&
      getObjLength(brokerDetails) &&
      !brokerDetails?.success
    ) {
      startTimer(10);
      setInValidBroker(true);
    }
  }, [brokerDetails]);

  useEffect(() => {
    if (getObjLength(brokerDetails) && !brokerDetails?.success && timer === 0) {
      closeBrokersModal();
    }
  }, [timer]);

  useEffect(() => {
    let active = true;
    loadCmsSeoCache().finally(() => {
      if (!active) {
        return;
      }
      const schemaData = setMetaDataForSEO(
        pathname,
        window?.location?.hostname,
      );
      setSchemaData(schemaData);
    });

    return () => {
      active = false;
      const metaDescription = document.querySelector(
        'meta[name="description"]',
      );
      if (metaDescription) {
        document.head.removeChild(metaDescription);
      }
    };
  }, [location, pathname]);

  // Call useInjectSchema at the top level (following React rules)
  useInjectSchema(pathname, schemaData); //Injects and removes script dynamically

  useEffect(() => {
    console.log("siteURL==>", siteHost);
    let isProvider = null;
    if (siteHost.split(".")[0]?.includes(SUBDOMAIN?.servicer)) {
      isProvider = siteHost;
      if (pathname === "/" || pathname === "/property-risk-management") {
        setSiteURL(DEFAULT_URL + pathname?.replace("/", ""));
      } else {
        setSiteURL(window?.location?.href);
      }
    } else {
      setSiteURL(window?.location?.href);
    }
    console.log(siteURL, "servicer==>", isProvider);
  }, [siteHost, pathname]);

  useEffect(() => {
    // to manage the scroll in mobile
    window.addEventListener("scroll", () => {
      manageMobileCookieBannerPosition();
    });
    setTimeout(() => {
      manageMobileCookieBannerPosition();
    }, 1000);
  }, []);

  // If this county is an investor vertical, force all routes to redirect to homepage
  useEffect(() => {
    if (countyData?.data?.is_county_investor) {
      const currentPath = location?.pathname || window.location.pathname;
      dispatch(propertyRiskManagementAction.fetchCountyData({}));
      if (currentPath !== "/") {
        navigate("/", { replace: true });
      }
    }
  }, [countyData, location?.pathname, navigate]);

  // Validation for new prefix/company pattern
  useEffect(() => {
    if (prefix && allowedPrefixObj) {
      if (allowedPrefixObj?.dynamic_companyName) {
        setCustomPrefixNavLinks(`/${prefix}/${company}/`);
      } else {
        setCustomPrefixNavLinks(`/${prefix}/`);
      }
    } else {
      setCustomPrefixNavLinks(undefined);
    }
  }, [prefix, navigate]);

  const fromCountyRecorder = localStorage.getItem("scrp_hp");
  const isCountySite =
    !fromCountyRecorder &&
    findCountyLocation(location?.pathname?.split("/")[1]);
  const countySiteData = isCountySite
    ? findCountyLocationData(location?.pathname?.split("/")[1])
    : null;

  return (
    <>
      <Helmet>
        {/* window.location.href will get URL with sub domain also so no duplicated URL will encounter in this */}
        <link rel="canonical" href={siteURL} />
      </Helmet>

      <Suspense fallback={<Loader open={true} />}>
        <ScrollToTop />
        <Routes>
          {
            (window.location.hostname
              .split(".")[0]
              ?.includes(SUBDOMAIN?.equityprotect) ||
              window.location.hostname
                .split(".")[0]
                ?.includes(SUBDOMAIN?.provider)) &&
            window.location.hostname.split(".")[0] !== "staging" ? (
              <>
                {lannerRestrictedRoutes.map(
                  ({ component, path, title, role }, key) => (
                    <Route
                      path={path}
                      element={
                        <>
                          <RoleProtectedRoute accessEntity={role}>
                            {component}
                          </RoleProtectedRoute>
                        </>
                      }
                      key={key}
                    />
                  ),
                )}

                {/* public route */}
                {lannerPublicRoutes?.map(({ component, path }, key) => (
                  <Route path={path} element={<>{component}</>} key={key} />
                ))}
              </>
            ) : window.location.hostname.split(".")[0] === SUBDOMAIN.try &&
              window.location.hostname.split(".")[0] !== "staging" ? (
              <>
                {/* try domain public routes */}
                {tryDomainRoutes.map(({ component, path, title }, key) => (
                  <Route
                    path={path}
                    element={
                      <>
                        <TryDomainNavBar />
                        {component}
                        {/* <Footer isTryPage={true} /> */}
                      </>
                    }
                    key={key}
                  />
                ))}
              </>
            ) : window.location.hostname
                .split(".")[0]
                ?.includes(SUBDOMAIN.servicer) &&
              window.location.hostname.split(".")[0] !== "staging" ? (
              <>
                {serviceProviderRoutes.map(
                  ({ component, path, title }, key) => (
                    <Route
                      path={path}
                      element={
                        <>
                          {Mobile_CookieConsent()}
                          <ServiceProvidersAdminNavbar />
                          <div
                            className={`${
                              openAlert &&
                              userPreferences &&
                              "mobile_cookie_mini_sidebar_spacing"
                            }`}
                          >
                            {component}
                            {cookieConsent()}
                          </div>
                        </>
                      }
                      key={key}
                    />
                  ),
                )}

                {/* public route */}
                {serviceProviderPublicRoutes?.map(
                  (
                    {
                      component,
                      path,
                      title,
                      role,
                      isLandingPage = false,
                      hideNavbar = false,
                    },
                    key,
                  ) => (
                    <Route
                      path={path}
                      element={
                        <>
                          {Mobile_CookieConsent()}

                          {!hideNavbar && (
                            <NavBar
                              isLandingPage={isLandingPage}
                              roles={[ROLES?.serviceProvider]}
                            />
                          )}
                          <div
                            className={`${
                              openAlert &&
                              userPreferences &&
                              "mobile_cookie_mini_sidebar_spacing"
                            }`}
                          >
                            {component}
                            {cookieConsent()}
                          </div>
                          <LazyLoadComponent threshold={150}>
                            <Footer isTryPage={false} />
                          </LazyLoadComponent>
                        </>
                      }
                      key={key}
                    />
                  ),
                )}

                {forServiceProvicerRoute?.map(
                  (
                    {
                      component,
                      path,
                      title,
                      role,
                      hideNavbar = false,
                      footer = true,
                    },
                    key,
                  ) => (
                    <Route
                      path={path}
                      element={
                        <>
                          {Mobile_CookieConsent()}
                          {!hideNavbar && (
                            <NavBar roles={[ROLES?.serviceProvider]} />
                          )}
                          <div
                            className={`${
                              openAlert &&
                              userPreferences &&
                              "mobile_cookie_mini_sidebar_spacing"
                            }`}
                          >
                            {component}
                            {cookieConsent()}
                          </div>
                          {footer && (
                            <LazyLoadComponent threshold={150}>
                              <Footer isTryPage={false} />
                            </LazyLoadComponent>
                          )}
                        </>
                      }
                      key={key}
                    />
                  ),
                )}

                {/* loggedIn Servicer Routes */}
                {serviceProviderLoginRoutes.map(
                  ({ component, path, title, role }, key) =>
                    role.includes(ROLES.serviceProvider) ? (
                      <Route
                        path={path}
                        element={
                          <>
                            <RouteProtection>
                              <RoleProtectedRoute accessEntity={role}>
                                {Mobile_CookieConsent()}
                                <div
                                  className={`${
                                    openAlert &&
                                    userPreferences &&
                                    "mobile_cookie_mini_sidebar_spacing"
                                  }`}
                                >
                                  <ServicerSideBar
                                    comp={component}
                                    key={key}
                                    isTailwindComponent={IS_TAILWIND_COMPONENT}
                                  />
                                </div>
                              </RoleProtectedRoute>
                            </RouteProtection>
                          </>
                        }
                        key={key}
                      />
                    ) : (
                      <Route path="/" element={<PageNotFound />} /> //404
                    ),
                )}
              </>
            ) : isCarHost() ? (
              <>
                {carHostRoutes.map(
                  (
                    {
                      component,
                      path,
                      hideNavbar = false,
                      hideFooter = false,
                    },
                    key,
                  ) => {
                    const hideChrome = hideNavbar || isNativeAppShell();
                    return (
                      <Route
                        path={path}
                        element={
                          <>
                            {Mobile_CookieConsent()}
                            {cookieConsent()}
                            <div className="tailwind_component">
                              {/* Shared public routes carry headerProps for the
                                  customer site; every CAR page uses the CAR navbar. */}
                              {!hideChrome && (
                                <TailwindHeader {...CAR_HEADER_PROPS} />
                              )}
                              <div
                                className={`${
                                  openAlert &&
                                  userPreferences &&
                                  "mobile_cookie_spacing"
                                }`}
                              >
                                {component}
                              </div>
                              {!hideFooter && !isNativeAppShell() && (
                                <TailwindFooter />
                              )}
                            </div>
                          </>
                        }
                        key={key}
                      />
                    );
                  },
                )}
              </>
            ) : (
              // window.location.hostname.split(".").length === 1 ? (
              // || window.location.hostname.split(".")[0] === SERVER
              <>
                {/*------- public routes -------*/}
                {publicRoutes.map(
                  (
                    {
                      component,
                      path,
                      title,
                      hideNavbar,
                      isNavLinkLeftAlign = false,
                      hide_my_acc_btn = false,
                      isLandingPage = false,
                      stickyNavbar = false,
                      hideServiceProviderFromNavbar = false,
                      footer = true,
                      isAttorneyRoute = false,
                      isIndustryPartnerRoute = false,
                      isInvestors = false,
                      isInvestorsButton = false,
                      isSiteMap = true,
                      hide_footerlinks = false,
                      hide_nav_links = false,
                      hide_call_us_now = false,
                      hide_footer_CTA = false,
                      isCountyVertical = false,
                    },
                    key,
                  ) => (
                    <Route
                      path={
                        isCountyVertical ? (isCountySite ? path : "/") : path
                      }
                      element={
                        // TEMPORARY route
                        title === "Lennar" ? (
                          component
                        ) : (
                          <>
                            {!isBot && flag && Mobile_CookieConsent()}
                            {!hideNavbar && (
                              <NavBar
                                isLandingPage={isLandingPage}
                                stickyNavbar={stickyNavbar}
                                hideServiceProviderFromNavbar={
                                  custom_prefix_nav_links
                                    ? true
                                    : hideServiceProviderFromNavbar
                                }
                                isNavLinkLeftAlign={
                                  isCountySite ? true : isNavLinkLeftAlign
                                }
                                isAttorneyRoute={isAttorneyRoute}
                                isIndustryPartnerRoute={isIndustryPartnerRoute}
                                isInvestors={isCountySite ? true : isInvestors}
                                isInvestorsButton={isInvestorsButton}
                                hide_my_acc_btn={
                                  isCountySite || custom_prefix_nav_links
                                    ? true
                                    : hide_my_acc_btn
                                }
                                countySiteData={countySiteData}
                                // to manage the custom links for navbar options
                                custom_prefix_nav_links={
                                  custom_prefix_nav_links
                                }
                                hideTitleTheft={custom_prefix_nav_links}
                                hide_nav_links={hide_nav_links}
                                hide_call_us_now={hide_call_us_now}
                              />
                            )}
                            <div
                              className={`${
                                openAlert &&
                                userPreferences &&
                                "mobile_cookie_spacing"
                              }`}
                            >
                              {path?.includes("pricing") && isCountySite ? (
                                <InvestorPricing
                                  countySiteData={countySiteData}
                                /> // for county vertical pricing
                              ) : (
                                component
                              )}
                            </div>
                            {footer &&
                              !pathname.includes(
                                "/eldocountyca/properties",
                              ) && (
                                <LazyLoadComponent threshold={150}>
                                  <Footer
                                    isTryPage={false}
                                    isAttorneyRoute={isAttorneyRoute}
                                    isIndustryPartnerRoute={
                                      isIndustryPartnerRoute
                                    }
                                    isInvestors={
                                      isCountySite ? true : isInvestors
                                    }
                                    isSiteMap={isSiteMap}
                                    hide_my_acc_btn={
                                      custom_prefix_nav_links
                                        ? custom_prefix_nav_links
                                        : hide_my_acc_btn
                                    }
                                    hide_footer_CTA={
                                      custom_prefix_nav_links
                                        ? custom_prefix_nav_links
                                        : hide_footer_CTA
                                    }
                                    hide_footerlinks={
                                      custom_prefix_nav_links
                                        ? custom_prefix_nav_links
                                        : hide_footerlinks
                                    }
                                    countySiteData={countySiteData}
                                  />
                                </LazyLoadComponent>
                              )}

                            {!isBot && flag && cookieConsent()}
                          </>
                        )
                      }
                      key={key}
                    />
                  ),
                )}

                {/*------- public routes v2 -------*/}
                {publicRoutesV2.map(
                  (
                    {
                      component,
                      path,
                      hideNavbar = false,
                      hideFooter = false,
                      headerProps = {},
                    },
                    key,
                  ) => {
                    // Runtime check: module-level flags can miss the iOS
                    // bootstrap that injects isIos before content loads.
                    const hideChrome = hideNavbar || isNativeAppShell();
                    return (
                    <Route
                      path={path}
                      element={
                        <>
                          {Mobile_CookieConsent()}
                          {cookieConsent()}
                          <div className="tailwind_component">
                            {!hideChrome && (
                              <TailwindHeader {...headerProps} />
                            )}
                            <div
                              className={`${
                                openAlert &&
                                userPreferences &&
                                "mobile_cookie_spacing"
                              }`}
                            >
                              {component}
                            </div>
                            {!hideFooter && !isNativeAppShell() && (
                              <TailwindFooter />
                            )}
                          </div>
                        </>
                      }
                      key={key}
                    />
                    );
                  },
                )}
                {/*------- public routes v2 -------*/}
                {blogRoutesV2.map(
                  (
                    { component, path, hideNavbar = false, hideFooter = false },
                    key,
                  ) => {
                    const hideChrome = hideNavbar || isNativeAppShell();
                    return (
                    <Route
                      path={path}
                      element={
                        <>
                          {Mobile_CookieConsent()}
                          {cookieConsent()}
                          <div className="tailwind_component">
                            {!hideChrome && <TailwindHeader />}
                            <div
                              className={`${
                                openAlert &&
                                userPreferences &&
                                "mobile_cookie_spacing"
                              }`}
                            >
                              {component}
                            </div>
                            {!hideFooter && !isNativeAppShell() && (
                              <TailwindFooter />
                            )}
                          </div>
                        </>
                      }
                      key={key}
                    />
                    );
                  },
                )}

                {SERVER !== "production" ? (
                  <Route
                    path="/lead"
                    element={
                      <>
                        <TryDomainNavBar />
                        <LeadPage />
                      </>
                    }
                    key={"lead"}
                  />
                ) : null}

                {/*------- user-admin routers -------*/}
                {userAdminRoutes.map(
                  ({ component, path, title, footer, hideLoginURL }, key) => (
                    <Route
                      path={path}
                      element={
                        <>
                          {Mobile_CookieConsent()}
                          <UserAdminNavBar hideLoginURL={hideLoginURL} />
                          <div
                            className={`${
                              openAlert &&
                              userPreferences &&
                              "mobile_cookie_spacing"
                            }`}
                          >
                            {component}
                          </div>
                          {cookieConsent()}
                          {footer && <Footer isTryPage={false} />}
                        </>
                      }
                      key={key}
                    />
                  ),
                )}

                {/*------- logged in users router -------*/}
                {loggedInUserRoutes.map(
                  ({ component, path, title, role }, key) =>
                    role.includes(ROLES.customer) ||
                    role.includes(ROLES.secondaryUser) ? (
                      <Route
                        path={path}
                        element={
                          <>
                            <RouteProtection>
                              <SignupCompletionGuard>
                                {Mobile_CookieConsent()}
                                <div
                                  className={`${
                                    openAlert &&
                                    userPreferences &&
                                    "mobile_cookie_mini_sidebar_spacing"
                                  }`}
                                >
                                  <RoleProtectedRoute accessEntity={role}>
                                    <MiniSidebar
                                      comp={component}
                                      isTailwindComponent={
                                        IS_TAILWIND_COMPONENT
                                      }
                                    />
                                  </RoleProtectedRoute>
                                </div>
                              </SignupCompletionGuard>
                            </RouteProtection>
                            {cookieConsent()}
                          </>
                        }
                        key={key}
                      />
                    ) : (
                      <Route path="/" element={<PageNotFound />} />
                    ),
                )}
                {/* Admin Routes — shared shell so sidebar/profile persist across navigation */}
                <Route
                  element={
                    <RouteProtection>
                      {Mobile_CookieConsent()}
                      <div
                        className={`${
                          openAlert &&
                          userPreferences &&
                          "mobile_cookie_mini_sidebar_spacing"
                        }`}
                      >
                        <AdminSideBar />
                      </div>
                    </RouteProtection>
                  }
                >
                  {adminRoutes.map(
                    (
                      { component, path, role, allowedUCCAdmin = false },
                      key,
                    ) =>
                      role.includes(ROLES.admin) ||
                      role.includes(ROLES.subAdmin) ||
                      role.includes(ROLES.basicAccess) ||
                      role.includes(ROLES.marketingAdmin) ||
                      role.includes(ROLES.provider) ||
                      role.includes(ROLES.AffiliateManager) ||
                      role.includes(ROLES.countyRecorder) ? (
                        <Route
                          path={path}
                          key={key}
                          element={
                            <RoleProtectedRoute
                              accessEntity={
                                role.includes(ROLES.subAdmin) &&
                                decryptPayload(
                                  localStorage.getItem("ucc_Allowed_Admin"),
                                )
                                  ? allowedUCCAdmin
                                    ? role
                                    : []
                                  : role
                              }
                            >
                              {component}
                            </RoleProtectedRoute>
                          }
                        />
                      ) : null,
                  )}
                </Route>

                {/* Professional Routes */}
                {professionalRoutes.map(
                  ({ component, path, title, role }, key) =>
                    role.includes(ROLES.professional) ? (
                      <Route
                        path={path}
                        element={
                          <>
                            <RouteProtection isIOS={code}>
                              <RoleProtectedRoute
                                accessEntity={role}
                                isIOS={code}
                              >
                                {Mobile_CookieConsent()}
                                <div
                                  className={`${
                                    openAlert &&
                                    userPreferences &&
                                    "mobile_cookie_mini_sidebar_spacing"
                                  }`}
                                >
                                  <ProfessionalSideBar
                                    comp={component}
                                    key={key}
                                  />
                                </div>
                              </RoleProtectedRoute>
                            </RouteProtection>
                          </>
                        }
                        key={key}
                      />
                    ) : (
                      <Route path="/" element={<PageNotFound />} /> //404
                    ),
                )}
              </>
            )

            // ) : (
            //   <>
            //     <Route path="/*" element={<Navigate to="/" />} /> //404
            //     <Route path="/" element={<PageNotFound />} /> //404
            //   </>
            // )
          }
          <Route path="/*" element={<Navigate to="/" />} /> {/* 404 */}
          <Route
            path={siteHost?.includes(SUBDOMAIN?.provider) ? "/404" : "/"}
            element={<PageNotFound />}
          />{" "}
          {/* 404 */}
        </Routes>
      </Suspense>

      {inValidBroker && (
        <AlertDialog
          type={"Error"}
          openPopup={true}
          onClose={true}
          onCloseFun={closeBrokersModal}
          mesage={
            <p>
              <b>{brokerDetails?.data?.message || "Something went wrong"}</b>
              <br />
              <span>
                You will be taken to the homepage shortly{" "}
                <b>({formatTimeForResendOTP(timer)})</b>. For additional
                assistance, kindly contact our support team.
              </span>
            </p>
          }
        />
      )}

      <Loader open={(profileLoader && !isAdminRoute) || brokerAgentLoader} />
      {SERVER === "production" ? (
        <script type="text/javascript">
          {" "}
          (function(){" "}
          {document.addEventListener(
            "submit",
            function (e) {
              // 1. Find the hidden field we added to the form
              var blocker = e.target.querySelector(".hs-identity-blocker");
              if (blocker) {
                // 2. Clear the hidden field value
                blocker.value = "";
                // 3. Immediately tell the HubSpot Tracker to "Forget" the session
                var _hsq = (window._hsq = window._hsq || []);
                _hsq.push(["doNotTrack"]);
                // 4. Force-delete the cookie from the browser storage
                document.cookie =
                  "hubspotutk=; Max-Age=-999; path=/; domain=" +
                  window.location.hostname;
                document.cookie =
                  "hubspotutk=; Max-Age=-999; path=/; domain=." +
                  window.location.hostname;
              }
            },
            true,
          )}
          )();
        </script>
      ) : null}
    </>
  );
};
export default RootRouter;
